import { parseChatbotInteractiveButtons } from "./chatbotQuickReplies";
import {
  extractFileNameFromUrl,
  resolveSimulatorAttachmentKind,
} from "./agentSimulatorAttachmentKind";

function resolveUserMessageSlotId(dataMsg) {
  const matched = Number(dataMsg?.matchedSlotId) || 0;
  if (matched > 0) return matched;
  const context = Number(dataMsg?.inputContextSlotId) || 0;
  if (context > 0) return context;
  return null;
}

function normalizeUraArquivo(raw) {
  if (raw == null) return null;
  if (typeof raw === "string") {
    const url = raw.trim();
    return url ? { url, nome: "", legenda: "", tipo: "" } : null;
  }
  if (typeof raw === "object") {
    const url = String(raw.url || raw.fileUrl || raw.arquivo || raw.link || "").trim();
    if (!url) return null;
    return {
      url,
      nome: String(raw.nome || raw.fileName || raw.name || raw.filename || "").trim(),
      legenda: String(raw.legenda || raw.caption || raw.content || "").trim(),
      tipo: String(raw.tipo || raw.messageType || raw.kind || "").trim(),
      contentType: String(raw.contentType || "").trim(),
    };
  }
  return null;
}

function attachmentBubbleFromUraArquivo(arq, slotId) {
  const normalized = normalizeUraArquivo(arq);
  if (!normalized?.url) return null;
  const fileName =
    normalized.nome || extractFileNameFromUrl(normalized.url) || "Anexo";
  const kind = resolveSimulatorAttachmentKind({
    messageType: normalized.tipo || null,
    contentType: normalized.contentType,
    url: normalized.url,
    fileName,
  });
  return {
    from: "bot",
    text: normalized.legenda || "",
    buttons: [],
    buttonOptions: [],
    linkButtons: [],
    slotId: slotId || null,
    attachment: {
      kind,
      url: normalized.url,
      fileName,
      contentType: normalized.contentType,
      messageType: normalized.tipo || null,
    },
  };
}

function collectSegmentArquivos(seg, dataMsg, isLast, segmentsHadFiles) {
  const fromSeg = Array.isArray(seg?.arquivos) ? seg.arquivos : [];
  if (fromSeg.length) return fromSeg;
  if (isLast && !segmentsHadFiles && Array.isArray(dataMsg?.arquivos)) return dataMsg.arquivos;
  return [];
}

/** Converte resposta URA (API) em mensagens do simulador. */
export function botMessagesFromUraResponse(dataMsg) {
  if (!dataMsg || typeof dataMsg !== "object") return [];

  const segments =
    Array.isArray(dataMsg.mensagens) && dataMsg.mensagens.length
      ? dataMsg.mensagens
      : [
          {
            text: dataMsg.mensagem || dataMsg.retorno || "",
            slotId: Number(dataMsg.matchedSlotId) || Number(dataMsg.inputContextSlotId) || 0,
            arquivos: dataMsg.arquivos,
          },
        ];

  const segmentsHadFiles = segments.some(
    (s) => Array.isArray(s?.arquivos) && s.arquivos.length > 0
  );

  const out = [];
  segments.forEach((seg, i) => {
    const isLast = i === segments.length - 1;
    const botText = String(seg.text ?? seg.mensagem ?? "").trim();
    const buttonSource = {
      text: botText,
      buttonMessage: seg?.buttonMessage ?? (isLast ? dataMsg.buttonMessage : null),
      botoes: seg?.botoes ?? (isLast ? dataMsg.botoes : null),
      buttons: seg?.buttons ?? (isLast ? dataMsg.buttons : null),
      listMessage: seg?.listMessage ?? (isLast ? dataMsg.listMessage : null),
      interactiveOptions:
        seg?.interactiveOptions ?? (isLast ? dataMsg.interactiveOptions : null),
    };
    const { replies, links, text: enrichedText } = parseChatbotInteractiveButtons(buttonSource);
    const buttons = replies.map((entry) => entry.label);
    const buttonOptions = replies.map((entry) => ({
      label: entry.label,
      id: String(entry.id || "").trim(),
    }));
    const linkButtons = links;
    const displayText = links.length && enrichedText != null ? String(enrichedText).trim() : botText;
    const slotId = Number(seg.slotId) || null;
    if (displayText || buttons.length || linkButtons.length) {
      out.push({
        from: "bot",
        text: displayText,
        buttons,
        buttonOptions,
        linkButtons,
        slotId,
      });
    }

    const arquivos = collectSegmentArquivos(seg, dataMsg, isLast, segmentsHadFiles);
    for (const arq of arquivos) {
      const bubble = attachmentBubbleFromUraArquivo(arq, slotId);
      if (bubble) out.push(bubble);
    }
  });

  return out;
}

/**
 * Respostas do Agente de IA após handoff híbrido (mesmo turno da URA ou turnos seguintes).
 * API: agentMensagens[] / agentReply (+ agentName opcional).
 * Cada segmento pode trazer texto, botões e/ou anexos (ex.: mapa de assentos).
 */
export function botMessagesFromAgentResponse(dataMsg) {
  if (!dataMsg || typeof dataMsg !== "object") return [];

  const agentName = dataMsg.agentName ? String(dataMsg.agentName).trim() : null;
  const segments =
    Array.isArray(dataMsg.agentMensagens) && dataMsg.agentMensagens.length
      ? dataMsg.agentMensagens
      : dataMsg.agentReply
        ? [{ text: dataMsg.agentReply, attachments: dataMsg.attachments }]
        : [];

  const out = [];
  for (const seg of segments) {
    const text = String(seg?.text ?? seg?.mensagem ?? "").trim();
    const { replies, links, text: enrichedText } = parseChatbotInteractiveButtons({
      ...seg,
      text,
      buttonMessage: seg?.buttonMessage ?? null,
      interactiveOptions: seg?.interactiveOptions ?? null,
    });
    const buttons = replies.map((entry) => entry.label);
    const buttonOptions = replies.map((entry) => ({
      label: entry.label,
      id: String(entry.id || "").trim(),
    }));
    const linkButtons = links;
    const displayText = links.length && enrichedText != null ? String(enrichedText).trim() : text;

    const attachmentList = [];
    if (seg?.attachment) attachmentList.push(seg.attachment);
    if (Array.isArray(seg?.attachments)) attachmentList.push(...seg.attachments);
    if (Array.isArray(seg?.arquivos)) attachmentList.push(...seg.arquivos);

    const rawCaptions = attachmentList
      .map((att) => String(att?.content || att?.caption || att?.legenda || "").trim())
      .filter(Boolean);
    const textIsAttachmentCaption = Boolean(displayText && rawCaptions.includes(displayText));

    const attachmentBubbles = attachmentList
      .map((arq) => {
        const bubble = attachmentBubbleFromUraArquivo(arq, null);
        if (!bubble) return null;
        // Mantém legenda no balão da imagem; evita duplicar o mesmo texto como balão separado.
        if (textIsAttachmentCaption && !bubble.text) {
          bubble.text = displayText;
        }
        return { ...bubble, agentName, fromAgent: true };
      })
      .filter(Boolean);

    if ((displayText || buttons.length || linkButtons.length) && !textIsAttachmentCaption) {
      out.push({
        from: "bot",
        text: displayText,
        buttons,
        buttonOptions,
        linkButtons,
        slotId: null,
        agentName,
        fromAgent: true,
      });
    } else if (!displayText && (buttons.length || linkButtons.length)) {
      out.push({
        from: "bot",
        text: "",
        buttons,
        buttonOptions,
        linkButtons,
        slotId: null,
        agentName,
        fromAgent: true,
      });
    }

    out.push(...attachmentBubbles);
  }

  // Fallback: anexos no payload raiz (legado reply + attachments).
  if (
    !segments.some(
      (s) =>
        s?.attachment ||
        (Array.isArray(s?.attachments) && s.attachments.length) ||
        (Array.isArray(s?.arquivos) && s.arquivos.length)
    ) &&
    Array.isArray(dataMsg.attachments)
  ) {
    for (const arq of dataMsg.attachments) {
      const bubble = attachmentBubbleFromUraArquivo(arq, null);
      if (bubble) out.push({ ...bubble, agentName, fromAgent: true });
    }
  }

  return out;
}

export function buildSimulatorTurnMessages(dataMsg, userText) {
  const trimmed = String(userText || "").trim();
  const userSlotId = resolveUserMessageSlotId(dataMsg);
  const userMessage = trimmed
    ? { from: "user", text: trimmed, slotId: userSlotId }
    : null;
  const botMessages = [
    ...botMessagesFromUraResponse(dataMsg),
    ...botMessagesFromAgentResponse(dataMsg),
  ];
  return userMessage ? [userMessage, ...botMessages] : botMessages;
}

/** Último passo do bot exibido no simulador (prompt atual aguardando resposta). */
export function resolveSimulatorActiveSlotId(messages) {
  if (!Array.isArray(messages) || !messages.length) return null;
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const msg = messages[i];
    if (msg?.from !== "bot") continue;
    // Mensagens do Agente de IA não pertencem a um slot URA — não sobrescrevem o destaque do fluxo.
    if (msg.fromAgent) continue;
    const slotId = Number(msg.slotId) || 0;
    if (slotId > 0) return slotId;
  }
  return null;
}

export { resolveUserMessageSlotId };
